import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Help, Picker, SystemText } from '@salesperformanceint/e4shared';
import SimpleAdmin, { DefaultOptions } from 'components/simpleAdmin';
import BlobImage from 'components/blobImage';
import CompetencyDetails from './components/competencyDetails';
import Preview from './components/preview';
import { hasModule } from 'helpers/profile';
import { setShowSuccess } from 'actions/ui';
import { apiGet, apiPost, apiPatch, apiDelete } from 'services';
import { adminLoading, handleError } from 'actions/admin';

export const AdminRoles = ({ history }) => {
    const dispatch = useDispatch();
    const [showUserPicker, setShowUserPicker] = React.useState(false);
    const [showCompetencyPicker, setShowCompetencyPicker] = React.useState(false);
    const [showCompetencyDetails, setShowCompetencyDetails] = React.useState(false);
    const [pickerItem, setPickerItem] = React.useState(null);
    const [showCRMPicker, setShowCRMPicker] = React.useState(false);
    const [showPreview, setShowPreview] = React.useState(false);
    const [showRealtimePicker, setShowRealtimePicker] = React.useState(false);
    const [showMetricsPicker, setShowMetricsPicker] = React.useState(false);
    const [competencies, setCompetencies] = React.useState([]);
    const [activeUsers, setActiveUsers] = React.useState([]);
    const [data, setData] = React.useState([]);
    const [dataLoading, setDataLoading] = React.useState(true);
    const [jobs, setJobs] = React.useState([]);
    const [systemNames, setSystemNames] = React.useState([]);
    const [integrationFields, setIntegrationFields] = React.useState([]);
    const [realtimeFields, setRealtimeFields] = React.useState([]);
    const [pickerRole, setPickerRole] = React.useState(null);
    const user = useSelector(state => state.user.user);

    const populateIntegrations = () => {
        apiGet('integration-fields', user.guid, response => {
            setIntegrationFields(response.filter(f => user.integrations.some(i => i.ref === f.ref)));
        });
    };

    React.useEffect(() => {
        const promises = [];
        dispatch(adminLoading(true));

        promises.push(apiGet('profile', user.guid, response => setActiveUsers(response)));
        promises.push(apiGet('role-system-names', user.guid, response => setSystemNames(response)));
        promises.push(
            apiGet('competency', user.guid, response =>
                setCompetencies(
                    response
                        .filter(c => c.active)
                        .map(r => ({ ...r, name: r.full_Name }))
                        .sort((a, b) => (a.name > b.name ? 1 : -1))
                )
            )
        );
        promises.push(apiGet('crm-job', user.guid, response => setJobs(response)));
        promises.push(apiGet('crm-realtime', user.guid, response => setRealtimeFields(response)));
        promises.push(
            apiGet('integration-fields', user.guid, response => {
                setIntegrationFields(response.filter(f => user.integrations.some(i => i.ref === f.ref)));
            })
        );

        Promise.all(promises).then(() => setDataLoading(false));
    }, []);

    React.useEffect(() => {
        api.load();
    }, [integrationFields]);

    const api = {
        load: () => {
            dispatch(adminLoading(true));
            apiGet('role', user.guid, response => {
                setData(
                    response.map(r => {
                        return {
                            ...r,
                            integration_Field_Ids: (r.integration_Field_Ids || []).filter(id =>
                                integrationFields.some(f => f.id === id)
                            ),
                            cannotDelete: !user.support && !!r.system_Name,
                        };
                    })
                );
                dispatch(adminLoading(false));
            });
        },
        add: (role, callback) => {
            apiPost(
                'role',
                role,
                user.guid,
                () => {
                    dispatch(setShowSuccess(SystemText('Admin/Roles/ShowSuccess/SaveRoles')));
                    callback();
                },
                error => {
                    handleError(dispatch, error);
                }
            );
        },
        update: (role, callback) => {
            apiPatch(
                'role',
                role,
                user.guid,
                () => {
                    dispatch(setShowSuccess(SystemText('Admin/Roles/ShowSuccess/SaveRoles')));
                    callback();
                },
                error => {
                    handleError(dispatch, error);
                }
            );
        },
        remove: (ids, callback) => {
            if (!Array.isArray(ids)) ids = [ids];
            ids.forEach((id, idx) => {
                apiDelete(`role/${id}`, null, user.guid, () => idx + 1 === ids.length && callback());
            });
        },
        clone: (id, callback) => {
            apiGet(`role-clone/${id}`, user.guid, () => {
                dispatch(setShowSuccess(SystemText('Admin/Roles/ShowSuccess/Clone')));
                callback();
            });
        },
    };

    const label = SystemText('Admin/Roles/Label/Options');

    const options = {
        ...DefaultOptions(),
        cannotDeleteMessage: SystemText('Admin/Table/Delete/System', label.toLowerCase()),
        label,
        extraOptions: [
            {
                btnClass: 'btn-primary',
                disabled: r => r.competency_Ids?.length === 0,
                label: SystemText('Admin/Roles/Options/Buttons/Label/ShowCompetencyDetails'),
                icon: 'fa-star',
                onClick: d => {
                    setShowCompetencyDetails(true);
                    setPickerRole(d);
                },
            },
            {
                btnClass: 'btn-primary',
                label: SystemText('Admin/Roles/Options/Buttons/Label/Clone'),
                icon: 'fa-clone',
                onClick: d => {
                    api.clone(d.id, populateIntegrations);
                },
            },
        ],
    };

    const showing = values =>
        user.support ? (values.mapped_System_Names?.length ? 'D' : 'T') : values.system_Name ? 'T' : 'D';

    const fields = [
        {
            label: SystemText('Admin/Roles/Fields/Label/SysName'),
            type: 'text',
            readonly: !user.support,
            dataProperty: 'system_Name',
            maxLength: 50,
            mandatory: true,
            hideField: (values, entity) => showing(values) === 'D',
            help: SystemText('Admin/Roles/Fields/Help/SysName'),
        },
        {
            label: SystemText('Admin/Roles/Fields/Label/Name'),
            type: 'text',
            mandatory: true,
            dataProperty: 'name',
            maxLength: 50,
            generalErrors: true,
            uniqueErrors: true,
            help: SystemText('Admin/Roles/Fields/Help/Name'),
        },
        {
            label: SystemText('Admin/Roles/Fields/Label/SysName'),
            type: 'select',
            data: systemNames.map(x => ({ name: x, id: x })),
            dataProperty: 'mapped_System_Names',
            allowMultiple: true,
            hideField: (values, entity) => showing(values) === 'T',
            help: SystemText('Admin/Roles/Fields/Help/SysName'),
        },
        {
            label: SystemText('Admin/Roles/Fields/Label/Competencies'),
            type: 'select',
            dataProperty: 'competency_Ids',
            data: competencies,
            allowMultiple: true,
            placeholder: SystemText('Admin/Roles/Fields/Placeholder/Competencies'),
            help: SystemText('Admin/Roles/Fields/Help/Competencies'),
        },
    ];

    if (user.crmIntegration && hasModule(user, 'integrations')) {
        fields.push({
            label: SystemText('Admin/Roles/Fields/CRM/Label'),
            type: 'select',
            help: SystemText('Admin/Roles/Fields/CRM/Help'),
            dataProperty: 'crm_Job_Ids',
            data: jobs,
            allowMultiple: true,
            placeholder: SystemText('Admin/Roles/Fields/CRM/Placeholder'),
        });

        fields.push({
            label: SystemText('Admin/Roles/Fields/Real/Label'),
            type: 'select',
            help: SystemText('Admin/Roles/Fields/Real/Help'),
            dataProperty: 'crm_Realtime_Ids',
            data: realtimeFields,
            allowMultiple: true,
            placeholder: SystemText('Admin/Roles/Fields/Real/Placeholder'),
            inTitleBar: true,
        });
    }

    user.integrations &&
        hasModule(user, 'integrations') &&
        user.integrations.forEach(i => {
            const filteredFields = integrationFields.filter(f => f.ref === i.ref);
            if (filteredFields.length > 0) {
                fields.push({
                    label: SystemText('Admin/Roles/Fields/Integration/Label', filteredFields[0].integration),
                    type: 'select',
                    help: SystemText('Admin/Roles/Fields/Integration/Help', filteredFields[0].integration),
                    dataProperty: 'integration_Field_Ids',
                    dataRef: i.ref,
                    data: filteredFields,
                    allowMultiple: true,
                    placeholder: SystemText('Admin/Roles/Fields/Integration/Placeholder', filteredFields[0].integration),
                    inTitleBar: true,
                });
            }
        });

    fields.push({
        label: SystemText('Admin/Roles/Fields/Label/Benchmarks'),
        type: 'toggle',
        mandatory: false,
        dataProperty: 'show_Benchmarks',
        help: SystemText('Admin/Roles/Fields/Help/Benchmarks'),
        inTitleBar: true,
    });

    const roleWarning = d => {
        let result = [];

        if (!user.support) return result;

        if (!d.system_Name && !d.mapped_System_Names?.length) {
            result = result.concat([<div>{SystemText('Admin/Roles/Table/Warning/SystemName')}</div>]);
        }

        if (!d.competency_Ids?.length) {
            result = result.concat([<div>{SystemText('Admin/Roles/Table/Warning/Competencies')}</div>]);
        }

        return result;
    };

    const tableColumns = [
        {
            label: SystemText('Admin/Roles/Table/Columns/Label/Role'),
            dataProperty: 'name',
            renderer: d => {
                const warning = roleWarning(d);
                return (
                    <>
                        <span>
                            {d.name}
                            {warning?.length > 0 && (
                                <Help id={`table-help-${d.id}`} target={<i className="fa fa-warning text-danger ml10" />}>
                                    {warning}
                                </Help>
                            )}
                        </span>
                    </>
                );
            },
        },
        {
            label: SystemText('Admin/Roles/Table/Columns/Label/Competencies'),
            dataProperty: 'competency_Ids',
            showCount: true,
            onClick: role => {
                setPickerRole(role);
                setShowCompetencyPicker(true);
            },
        },
        {
            label: SystemText('Admin/Roles/Table/Columns/Label/Users'),
            dataProperty: 'active_Users',
            showCount: true,
            onClick: role => {
                setPickerRole(role);
                setShowUserPicker(true);
            },
        },
    ];

    if (user.crmIntegration && hasModule(user, 'integrations')) {
        tableColumns.push({
            label: SystemText('Admin/Roles/Table/Columns/Label/CRM'),
            dataProperty: 'crm_Job_Ids',
            showCount: true,
            onClick: item => {
                setPickerItem(item);
                setShowCRMPicker(true);
            },
        });

        tableColumns.push({
            label: SystemText('Admin/Roles/Table/Columns/Label/Real'),
            dataProperty: 'crm_Realtime_Ids',
            showCount: true,
            onClick: item => {
                setPickerItem(item);
                setShowRealtimePicker(true);
            },
        });
    }

    if (integrationFields?.length > 0 && hasModule(user, 'integrations')) {
        tableColumns.push({
            label: SystemText('Admin/Roles/Table/Columns/Label/Other'),
            dataProperty: 'integration_Field_Ids',
            showCount: true,
            onClick: item => {
                setPickerItem(item);
                setShowMetricsPicker(true);
            },
        });
    }

    const extraButton = (
        <button className="ml5 btn btn-primary" onClick={() => setShowPreview(true)}>
            <i className="fa fa-binoculars mr5" />
            {SystemText('Admin/Roles/Buttons/Preview')}
        </button>
    );

    return (
        <div>
            <SimpleAdmin
                data={data}
                load={api.load}
                extraButtons={extraButton}
                add={api.add}
                update={api.update}
                remove={api.remove}
                fields={fields}
                options={options}
                columns={tableColumns}
                loading={dataLoading}
                defaultValues={{ show_Benchmarks: true }}
            />
            <Picker
                title={SystemText('Admin/Roles/Pickers/Title/Competencies', pickerRole?.name)}
                entity={SystemText('Admin/Roles/Pickers/Entity/Competencies')}
                data={competencies}
                value={pickerRole?.competency_Ids}
                show={showCompetencyPicker}
                onSave={ids => {
                    api.update({ ...pickerRole, competency_Ids: ids }, () => {
                        dispatch(setShowSuccess(SystemText('Admin/Roles/ShowSuccess/SaveCompetencies')));
                        setPickerRole(null);
                        setShowCompetencyPicker(false);
                        api.load();
                    });
                }}
                onClose={() => {
                    setPickerRole(null);
                    setShowCompetencyPicker(false);
                }}
                itemPrefix="star"
            />
            <Picker
                title={SystemText('Admin/Roles/Pickers/Title/Users', pickerRole?.name)}
                entity={SystemText('Admin/Roles/Pickers/Entity/Users')}
                data={activeUsers}
                nameProperty="full_Name"
                filterOption="staff"
                value={pickerRole?.active_Users?.map(u => u.id)}
                show={showUserPicker}
                onSave={ids => {
                    api.update({ ...pickerRole, active_User_Ids: ids }, () => {
                        dispatch(setShowSuccess(SystemText('Admin/Roles/ShowSuccess/SaveUsers')));
                        setPickerRole(null);
                        setShowUserPicker(false);
                        api.load();
                    });
                }}
                onClose={() => {
                    setPickerRole(null);
                    setShowUserPicker(false);
                }}
                itemPrefix={p => (
                    <div className="image-holder mr10">
                        <BlobImage folder="avatar" src={p.avatar} initials={p.initials} />
                    </div>
                )}
            />
            <Picker
                title={SystemText('Admin/Roles/Pickers/CRM/Label', pickerItem?.name)}
                entity={SystemText('Admin/Roles/Pickers/CRM')}
                data={jobs}
                value={pickerItem?.crm_Job_Ids}
                show={showCRMPicker}
                onSave={ids => {
                    api.update({ ...pickerItem, crm_Job_Ids: ids }, () => {
                        dispatch(setShowSuccess(SystemText('Admin/Roles/Pickers/CRM/Success')));
                        setShowCRMPicker(false);
                        api.load();
                    });
                }}
                onClose={() => {
                    setPickerItem(null);
                    setShowCRMPicker(false);
                    dispatch(setShowSuccess(false));
                }}
                itemPrefix="line-chart"
            />
            <Picker
                title={SystemText('Admin/Roles/Pickers/Real/Label', pickerItem?.name)}
                entity={SystemText('Admin/Roles/Pickers/Real')}
                data={realtimeFields}
                value={pickerItem?.crm_Realtime_Ids}
                show={showRealtimePicker}
                onSave={ids => {
                    api.update({ ...pickerItem, crm_Realtime_Ids: ids }, () => {
                        dispatch(setShowSuccess(SystemText('Admin/Roles/Pickers/Real/Success')));
                        setShowRealtimePicker(false);
                        api.load();
                    });
                }}
                onClose={() => {
                    setPickerItem(null);
                    setShowRealtimePicker(false);
                    dispatch(setShowSuccess(false));
                }}
                itemPrefix="line-chart"
            />
            {showMetricsPicker && (
                <Picker
                    title={SystemText('Admin/Roles/Pickers/Other/Label', pickerItem?.name)}
                    entity={SystemText('Admin/Roles/Pickers/Other')}
                    data={integrationFields}
                    nameProperty="full_Name"
                    value={pickerItem?.integration_Field_Ids}
                    show={showMetricsPicker}
                    onSave={ids => {
                        api.update({ ...pickerItem, integration_Field_Ids: ids }, () => {
                            dispatch(setShowSuccess(SystemText('Admin/Roles/Pickers/Other/Success')));
                            setShowMetricsPicker(false);
                            api.load();
                        });
                    }}
                    onClose={() => {
                        setPickerItem(null);
                        setShowMetricsPicker(false);
                        dispatch(setShowSuccess(false));
                    }}
                    itemPrefix="line-chart"
                />
            )}
            {pickerRole && (
                <CompetencyDetails
                    role={pickerRole}
                    show={showCompetencyDetails}
                    close={() => setShowCompetencyDetails(false)}
                />
            )}
            <Preview show={showPreview} close={() => setShowPreview(false)} roles={data} user={user} />
        </div>
    );
};

export default AdminRoles;
